import { v4 as uuidv4 } from 'uuid'

// Stable per-device anonymous identifier used as `external_id` for unauthenticated
// traffic. Lives in localStorage so it survives reloads and visits within the same
// browser profile. When the user signs in we replace it with the authenticated
// user id in the in-memory identity registry, but the stored value here is left
// alone — if they log out we revert to the same anon id.
//
// Meta CAPI's "External ID" parameter accepts any opaque per-user identifier;
// supplying a stable one for anonymous visitors lifts the External ID coverage
// from 0% to ~100% on anon traffic and gives Meta a deterministic dedupe key
// across browser-side pixel and server-side CAPI events for the same person.

const ANON_ID_KEY = 'hh.analytics.anon_id'

let cached: string | undefined

function readStored(): string | undefined {
  try {
    const v = localStorage.getItem(ANON_ID_KEY)
    return v ?? undefined
  } catch {
    return undefined
  }
}

function writeStored(value: string): void {
  try {
    localStorage.setItem(ANON_ID_KEY, value)
  } catch {
    // Storage unavailable (Safari private mode, quota). The in-memory `cached`
    // value still de-dupes within the page lifetime; cross-session continuity
    // is silently lost in that edge case.
  }
}

/**
 * Returns the anonymous external_id, creating and persisting it on first call.
 * Safe to call from SSR contexts — returns undefined when localStorage is
 * unreachable and no in-memory value has been minted yet.
 */
export function getAnonymousExternalId(): string | undefined {
  if (cached) return cached
  if (typeof window === 'undefined') return undefined

  const stored = readStored()
  if (stored) {
    cached = stored
    return cached
  }

  const fresh = uuidv4()
  writeStored(fresh)
  cached = fresh
  return cached
}
